import {
  detectMaxCols,
  trimEmptyEdges,
  parseAmount,
  inferNetwork,
  firstHeaderMatch,
  toEpochSeconds,
  inferNetworkFromAddrOrAsset,
  detectOnchainLedgerCsv,
  detectTxidFromToCsvV1,
  buildUtcTimestamp
} from '@/utils/interpreter_utils.js';
import {
  guessFormat,
  findHeaderRow,
  detectColumns,
  isMethodLikeHeader,
  normalizeMethodCell,
  looksLikeTransferSignature,
  termSets,
  detectBareTwoCols,
  maybeExplodeSingleCellCsv,
  sanitizeCell,
  headerSets,
  extractFirstAddressFromText,
  looksLikeSetDNotes,
  detectDebitCreditCashoutCsvV1,
  detectActivityDetailsCsvV1,
  detectStandardSetC,
  detectStandardSetD
} from '@/utils/txn_interpreter_core.js';

export const DEFAULT_TABULAR_IMPORT_OPTIONS = {
  maxRows: 20000,
  maxCols: 200,
  maxCellChars: 10000,
  trimWhitespace: true,
  neutralizeFormulas: true,
  assumeAllWithdrawalsWhenNoType: true,
  strictAnchorValidation: true,
  treatMethodTransferAsWithdrawal: true,
  columnOverrides: {
    txHash: -1,
    toAddress: -1,
    transferType: -1,
    asset: -1,
    amount: -1
  }
};

const HEADER_SENTINELS = new Set([
  'transaction id',
  'txid',
  'txn hash',
  'hash',
  'hash id',
  'hashid',
  'transaction hash',
  'to',
  'to address',
  'destination',
  'recipient',
  'address',
  'withdraw address',
  'from',
  'from address',
  'notes',
  'note',
  'memo',
  'description',
  'date',
  'time',
  'timestamp',
  'amount',
  'currency',
  'asset',
  'unknown',
  'na',
  'n/a',
  '-',
  '—'
]);

function normalizeLooseText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\u2014|\u2013/g, '-')
    .replace(/[\s_]+/g, ' ')
    .replace(/[^\w\- ]+/g, '')
    .trim();
}

function isHeaderish(value, headerLabel) {
  const normalized = normalizeLooseText(value);
  if (!normalized) return false;
  if (headerLabel && normalized === normalizeLooseText(headerLabel)) return true;
  return HEADER_SENTINELS.has(normalized);
}

function looksLikeTxHash(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  return /^0x[a-fA-F0-9]{64}$/.test(text) || /^[A-Fa-f0-9]{64}$/.test(text);
}

function looksLikeAddress(value) {
  const text = String(value || '').trim();
  if (!text) return false;

  return (
    /^0x[a-fA-F0-9]{40}$/.test(text)
    || /^bc1[0-9ac-hj-np-z]{25,62}$/i.test(text)
    || /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(text)
    || /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(text)
    || /^(X|P|C)-avax1[0-9ac-hj-np-z]{20,}$/i.test(text)
  );
}

function sanitizeRows(rawRows, options) {
  const cleanedOptions = {
    trimWhitespace: options.trimWhitespace,
    neutralizeFormulas: options.neutralizeFormulas,
    MAX_CELL_CHARS: options.maxCellChars
  };

  const exploded = maybeExplodeSingleCellCsv(rawRows || []);
  const trimmed = trimEmptyEdges(exploded);
  const cappedRows = trimmed.slice(0, options.maxRows);
  const maxCols = Math.min(options.maxCols, detectMaxCols(cappedRows));

  return cappedRows.map((row) =>
    (row || []).slice(0, maxCols).map((cell) => sanitizeCell(cell, cleanedOptions))
  );
}

function applyColumnOverrides(baseColumns, overrides) {
  return {
    txHash: overrides.txHash >= 0 ? overrides.txHash : baseColumns.txHash,
    toAddress: overrides.toAddress >= 0 ? overrides.toAddress : baseColumns.toAddress,
    transferType: overrides.transferType >= 0 ? overrides.transferType : baseColumns.transferType,
    asset: overrides.asset >= 0 ? overrides.asset : baseColumns.asset,
    amount: overrides.amount >= 0 ? overrides.amount : baseColumns.amount
  };
}

function resolveColumnSelection({
  hasHeader,
  headerRaw,
  headerNorm,
  detected,
  overrides
}) {
  const result = {
    columns: { txHash: -1, toAddress: -1, transferType: -1, asset: -1, amount: -1 },
    flags: {
      isLedgerCsvV1: false,
      isCsvTxidFromToV1: false,
      isCsvDebitCreditCashoutV1: false,
      isCsvActivityDetailsV1: false,
      isCsvStandardSetC: false,
      isCsvStandardSetD: false
    },
    maps: {
      ledgerMap: null,
      fromToMap: null,
      cashoutMap: null,
      activityMap: null,
      stdCMap: null,
      stdDMap: null
    },
    timeCols: {
      ledgerTimeCols: null,
      fromToTimeCol: -1,
      dcTimeCol: -1,
      activityTimeCol: -1,
      stdCTimeCol: -1,
      stdDTimeCol: -1,
      stdDNotesCol: -1
    }
  };

  if (!hasHeader) {
    result.columns = applyColumnOverrides(
      {
        txHash: detected.txHash,
        toAddress: detected.toAddress,
        transferType: detected.transferType,
        asset: detected.asset,
        amount: detected.amount
      },
      overrides
    );
    return result;
  }

  const ledgerMap = detectOnchainLedgerCsv(headerRaw, headerNorm);
  const fromToMap = detectTxidFromToCsvV1(headerRaw, headerNorm);
  const cashoutMap = detectDebitCreditCashoutCsvV1(headerRaw, headerNorm);
  const activityMap = detectActivityDetailsCsvV1(headerRaw, headerNorm);
  const stdCMap = detectStandardSetC(headerRaw, headerNorm);
  const stdDMap = detectStandardSetD(headerRaw, headerNorm);

  result.maps = {
    ledgerMap,
    fromToMap,
    cashoutMap,
    activityMap,
    stdCMap,
    stdDMap
  };

  if (activityMap?.isCsvActivityDetailsV1) {
    result.flags.isCsvActivityDetailsV1 = true;
    result.timeCols.activityTimeCol = typeof activityMap.timeCol === 'number' ? activityMap.timeCol : -1;
    result.columns = applyColumnOverrides({
      txHash: activityMap.columns.txHash,
      toAddress: activityMap.columns.toAddress,
      transferType: activityMap.columns.transferType,
      asset: activityMap.columns.asset,
      amount: activityMap.columns.amount
    }, overrides);
    return result;
  }

  if (cashoutMap?.isCsvDebitCreditCashoutV1) {
    result.flags.isCsvDebitCreditCashoutV1 = true;
    result.timeCols.dcTimeCol = typeof cashoutMap.timeCol === 'number' ? cashoutMap.timeCol : -1;
    result.timeCols.directionCol = typeof cashoutMap.directionCol === 'number' ? cashoutMap.directionCol : -1;
    result.columns = applyColumnOverrides({
      txHash: cashoutMap.columns.txHash,
      toAddress: cashoutMap.columns.toAddress,
      transferType: cashoutMap.columns.transferType,
      asset: cashoutMap.columns.asset,
      amount: cashoutMap.columns.amount
    }, overrides);
    return result;
  }

  if (stdCMap?.isCsvStandardSetC) {
    result.flags.isCsvStandardSetC = true;
    result.timeCols.stdCTimeCol = typeof stdCMap.timeCol === 'number' ? stdCMap.timeCol : -1;
    result.columns = applyColumnOverrides({
      txHash: stdCMap.columns.txHash,
      toAddress: stdCMap.columns.toAddress,
      transferType: stdCMap.columns.transferType,
      asset: stdCMap.columns.asset,
      amount: stdCMap.columns.amount
    }, overrides);
    return result;
  }

  if (stdDMap?.isCsvStandardSetD) {
    result.flags.isCsvStandardSetD = true;
    result.timeCols.stdDTimeCol = typeof stdDMap.timeCol === 'number' ? stdDMap.timeCol : -1;
    result.timeCols.stdDNotesCol = typeof stdDMap.notesCol === 'number' ? stdDMap.notesCol : -1;
    result.columns = applyColumnOverrides({
      txHash: stdDMap.columns.txHash,
      toAddress: -1,
      transferType: stdDMap.columns.transferType,
      asset: stdDMap.columns.asset,
      amount: stdDMap.columns.amount
    }, overrides);
    return result;
  }

  if (ledgerMap?.isLedgerCsvV1) {
    result.flags.isLedgerCsvV1 = true;
    result.timeCols.ledgerTimeCols = ledgerMap.timeCols;
    result.columns = applyColumnOverrides({
      txHash: ledgerMap.columns.txHash,
      toAddress: ledgerMap.columns.toAddress,
      transferType: ledgerMap.columns.transferType,
      asset: ledgerMap.columns.asset,
      amount: ledgerMap.columns.amount
    }, overrides);
    return result;
  }

  if (fromToMap?.isCsvTxidFromToV1) {
    result.flags.isCsvTxidFromToV1 = true;
    result.timeCols.fromToTimeCol = typeof fromToMap.timeCol === 'number' ? fromToMap.timeCol : -1;
    result.columns = applyColumnOverrides({
      txHash: fromToMap.columns.txHash,
      toAddress: fromToMap.columns.toAddress,
      transferType: fromToMap.columns.transferType,
      asset: fromToMap.columns.asset,
      amount: fromToMap.columns.amount
    }, overrides);
    return result;
  }

  result.columns = applyColumnOverrides({
    txHash: detected.txHash,
    toAddress: detected.toAddress,
    transferType: detected.transferType,
    asset: detected.asset,
    amount: detected.amount
  }, overrides);

  return result;
}

function buildRowWarnings({ txHash, toAddress, canLookupTxHash }) {
  const warnings = [];

  if (!txHash) {
    warnings.push('Transaction hash is missing.');
  }

  if (!toAddress) {
    warnings.push('Destination address was not detected.');
  }

  if (canLookupTxHash) {
    warnings.push('Enough anchors exist for a future hash lookup.');
  }

  return warnings;
}

export function analyzeTabularTransactionRows(rawRows, userOptions = {}) {
  const options = {
    ...DEFAULT_TABULAR_IMPORT_OPTIONS,
    ...userOptions,
    columnOverrides: {
      ...DEFAULT_TABULAR_IMPORT_OPTIONS.columnOverrides,
      ...(userOptions.columnOverrides || {})
    }
  };

  const rows = sanitizeRows(rawRows, options);
  if (!rows.length || !rows[0]?.length) {
    return {
      rows,
      format: null,
      hasHeader: false,
      headerRowIndex: -1,
      headerRaw: [],
      headerNorm: [],
      columns: { txHash: -1, toAddress: -1, transferType: -1, asset: -1, amount: -1 },
      detectionStats: {},
      entries: [],
      warnings: ['No usable rows were found in the file.'],
      importSummary: {
        candidateCount: 0,
        readyCount: 0,
        missingHashCount: 0,
        withToAddressCount: 0,
        invalidCount: 0
      },
      meta: {
        flags: {
          isLedgerCsvV1: false,
          isCsvTxidFromToV1: false,
          isCsvDebitCreditCashoutV1: false,
          isCsvActivityDetailsV1: false,
          isCsvStandardSetC: false,
          isCsvStandardSetD: false
        },
        fromAddressCol: -1,
        notesCol: -1,
        timeCol: -1,
        directionCol: -1,
        hasTypeColumn: false,
        engageNotesMode: false,
        ledgerTimeCols: null
      }
    };
  }

  const headerInfo = findHeaderRow(rows);
  const hasHeader = Boolean(headerInfo?.hasHeader);
  const headerRowIndex = hasHeader ? headerInfo.rowIndex : 0;
  const headerNorm = hasHeader ? headerInfo.header : [];
  const headerRaw = hasHeader ? (rows[headerRowIndex] || []).map((value) => String(value || '')) : [];

  let detected;
  if (hasHeader) {
    detected = detectColumns(rows, headerRowIndex, headerNorm, true);
  } else {
    const bare = detectBareTwoCols(rows);
    detected = {
      txHash: bare.txHash,
      toAddress: bare.toAddress,
      transferType: -1,
      asset: -1,
      amount: -1,
      _stats: {}
    };
  }

  const selection = resolveColumnSelection({
    hasHeader,
    headerRaw,
    headerNorm,
    detected,
    overrides: options.columnOverrides
  });

  const columns = selection.columns;
  const flags = selection.flags;
  const maps = selection.maps;
  const timeCols = selection.timeCols;

  const HS = headerSets();
  let fromAddressCol = -1;
  if (flags.isCsvTxidFromToV1 && maps.fromToMap?.columns && typeof maps.fromToMap.columns.fromAddress === 'number') {
    fromAddressCol = maps.fromToMap.columns.fromAddress;
  } else if (hasHeader) {
    fromAddressCol = firstHeaderMatch(headerNorm, HS.fromAddress);
  }

  const hasTypeColumn = typeof columns.transferType === 'number' && columns.transferType >= 0;
  let notesCol = hasHeader ? firstHeaderMatch(headerNorm, HS.notes) : -1;
  let timeCol = hasHeader ? firstHeaderMatch(headerNorm, ['timestamp', 'timestamp_utc', 'datetime', 'date', 'time']) : -1;

  if (flags.isCsvTxidFromToV1 && timeCols.fromToTimeCol >= 0) timeCol = timeCols.fromToTimeCol;
  if (flags.isCsvDebitCreditCashoutV1 && timeCols.dcTimeCol >= 0) timeCol = timeCols.dcTimeCol;
  if (flags.isCsvActivityDetailsV1 && timeCols.activityTimeCol >= 0) timeCol = timeCols.activityTimeCol;
  if (flags.isCsvStandardSetC && timeCols.stdCTimeCol >= 0) timeCol = timeCols.stdCTimeCol;
  if (flags.isCsvStandardSetD && timeCols.stdDTimeCol >= 0) timeCol = timeCols.stdDTimeCol;
  if (flags.isCsvStandardSetD && timeCols.stdDNotesCol >= 0) notesCol = timeCols.stdDNotesCol;

  const txHeaderLabelRaw = hasHeader && columns.txHash >= 0 ? String(headerRaw[columns.txHash] || '') : '';
  const toHeaderLabelRaw = hasHeader && columns.toAddress >= 0 ? String(headerRaw[columns.toAddress] || '') : '';

  const engageNotesMode =
    (flags.isCsvStandardSetD && notesCol >= 0)
    || (hasHeader && columns.txHash < 0 && looksLikeSetDNotes(headerNorm) && notesCol >= 0);

  const dataStart = hasHeader ? headerRowIndex + 1 : 0;
  const entries = [];
  const warnings = [];
  let assumedWithdrawalRows = 0;
  let anchorSanitizedSkips = 0;
  let amountParseTried = 0;
  let amountParseFailed = 0;

  for (let rowIndex = dataStart; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];

    const rawType = columns.transferType >= 0 ? row[columns.transferType] : '';
    const typeValueRaw = String(rawType || '').trim();
    const typeValue = typeValueRaw.toLowerCase();

    let amountDisplay = null;
    let amountRawAbs = null;
    let originalSignedAmount = null;
    if (columns.amount >= 0) {
      amountParseTried += 1;
      let amountCell = row[columns.amount];

      if ((flags.isLedgerCsvV1 || flags.isCsvTxidFromToV1 || flags.isCsvDebitCreditCashoutV1 || flags.isCsvActivityDetailsV1) && typeof amountCell === 'string') {
        amountCell = amountCell
          .replace(/\$/g, '')
          .replace(/,/g, '')
          .replace(/\s+/g, ' ')
          .replace(/^\((.*)\)$/, '-$1')
          .trim();
      }

      const parsedAmount = parseAmount(amountCell);
      if (!parsedAmount) {
        amountParseFailed += 1;
      } else {
        originalSignedAmount = parsedAmount.num;
        amountRawAbs = Math.abs(parsedAmount.num);
        amountDisplay = parsedAmount.disp.replace(/^-/, '');
      }
    }

    let isWithdrawal = false;

    if (flags.isLedgerCsvV1) {
      isWithdrawal = /\bon[-\s]?chain\b/i.test(typeValueRaw);
    } else if (flags.isCsvActivityDetailsV1) {
      const normalizedType = String(typeValueRaw || '')
        .toLowerCase()
        .replace(/[–—]/g, '-')
        .replace(/\s*-\s*/g, '-')
        .trim();
      isWithdrawal = normalizedType === 'withdrawal-crypto';
    } else if (flags.isCsvStandardSetC) {
      isWithdrawal = String(typeValueRaw || '').toLowerCase().trim() === 'crypto_withdrawal';
    } else if (flags.isCsvStandardSetD) {
      const normalizedType = String(typeValueRaw || '').toLowerCase().trim();
      isWithdrawal = ['send', 'sent', 'transfer_out', 'withdrawal', 'withdraw'].includes(normalizedType);
    } else if (flags.isCsvDebitCreditCashoutV1) {
      const directionColumn = typeof timeCols.directionCol === 'number' ? timeCols.directionCol : -1;
      const directionValue = directionColumn >= 0 ? String(row[directionColumn] || '').toLowerCase() : '';
      const looksCashout = typeValueRaw.toLowerCase() === 'crypto cashout';
      const looksOutgoing = /\b(send|out|debit|withdraw|withdrawal|external|to\s*wallet)\b/.test(directionValue);
      isWithdrawal = looksCashout && looksOutgoing;
    } else if (flags.isCsvTxidFromToV1) {
      isWithdrawal = true;
    } else {
      const transferHeaderIsMethod = (columns.transferType >= 0) && isMethodLikeHeader(headerNorm, columns.transferType);
      if (transferHeaderIsMethod && options.treatMethodTransferAsWithdrawal) {
        const normalizedMethod = normalizeMethodCell(rawType);
        const looksLikeSignature = looksLikeTransferSignature(rawType);
        const isTransferFrom = normalizedMethod === 'transfer_from' || normalizedMethod === 'transferfrom' || normalizedMethod.startsWith('transfer_from');
        const isOwnershipMethod = normalizedMethod.startsWith('transfer_ownership');
        const isMethodTransfer =
          looksLikeSignature
          || normalizedMethod === 'transfer'
          || normalizedMethod === 'erc20_transfer'
          || normalizedMethod === 'token_transfer'
          || (normalizedMethod.startsWith('transfer') && !isTransferFrom && !isOwnershipMethod);

        isWithdrawal = isMethodTransfer ? (originalSignedAmount != null ? originalSignedAmount < 0 : true) : false;
      } else {
        const { outTerms, inTerms } = termSets();
        if (typeValue) {
          if (outTerms.has(typeValue)) isWithdrawal = true;
          else if (inTerms.has(typeValue)) isWithdrawal = false;
        }
      }
    }

    const txHashBase = columns.txHash >= 0 ? String(row[columns.txHash] || '').trim() : '';
    let txHash = txHashBase;

    let toAddress = columns.toAddress >= 0 ? String(row[columns.toAddress] || '').trim() : '';
    if (flags.isCsvTxidFromToV1 && !toAddress) {
      isWithdrawal = false;
    }
    if (flags.isCsvDebitCreditCashoutV1 && !toAddress) {
      isWithdrawal = false;
    }

    const extractedAddress = extractFirstAddressFromText(toAddress);
    if (extractedAddress) {
      toAddress = extractedAddress;
    }

    if ((!toAddress || toAddress === '—') && (flags.isCsvStandardSetD || engageNotesMode) && notesCol >= 0) {
      const noteCell = row[notesCol];
      const extractedFromNotes = extractFirstAddressFromText(noteCell);
      if (extractedFromNotes) {
        toAddress = extractedFromNotes;
      }
    }

    const txHashBeforeSanitize = txHash;
    const toAddressBeforeSanitize = toAddress;

    if (isHeaderish(txHash, txHeaderLabelRaw)) txHash = '';
    if (isHeaderish(toAddress, toHeaderLabelRaw)) toAddress = '';

    if (options.strictAnchorValidation) {
      if (txHash && !looksLikeTxHash(txHash)) txHash = '';
      if (toAddress && !looksLikeAddress(toAddress)) toAddress = '';
    }

    let fromAddressRaw = '';
    if (fromAddressCol >= 0) {
      fromAddressRaw = String(row[fromAddressCol] ?? '').trim();
    }

    if (!isWithdrawal && !hasTypeColumn && options.assumeAllWithdrawalsWhenNoType) {
      const hasMappedDestinationColumn = typeof columns.toAddress === 'number' && columns.toAddress >= 0;
      const hasDestination = Boolean(toAddress);
      const hasHash = Boolean(txHash);
      if (hasDestination || (hasHash && hasMappedDestinationColumn)) {
        isWithdrawal = true;
        assumedWithdrawalRows += 1;
      }
    }

    let timestampValue = null;
    if (flags.isLedgerCsvV1 && timeCols.ledgerTimeCols) {
      const initDate = timeCols.ledgerTimeCols.initDate >= 0 ? row[timeCols.ledgerTimeCols.initDate] : null;
      const initTime = timeCols.ledgerTimeCols.initTime >= 0 ? row[timeCols.ledgerTimeCols.initTime] : null;
      timestampValue = buildUtcTimestamp(initDate, initTime);
      if (!timestampValue) {
        const completeDate = timeCols.ledgerTimeCols.compDate >= 0 ? row[timeCols.ledgerTimeCols.compDate] : null;
        const completeTime = timeCols.ledgerTimeCols.compTime >= 0 ? row[timeCols.ledgerTimeCols.compTime] : null;
        timestampValue = buildUtcTimestamp(completeDate, completeTime);
      }
    } else {
      timestampValue = timeCol >= 0 ? String(row[timeCol] || '').trim() : null;
    }
    const timestampEpoch = toEpochSeconds(timestampValue);

    if (hasHeader) {
      if (!isWithdrawal) continue;
      if (!txHash && !toAddress) {
        if (options.strictAnchorValidation && (txHashBeforeSanitize || toAddressBeforeSanitize)) {
          anchorSanitizedSkips += 1;
        }
        continue;
      }
    } else if (!txHash && !toAddress) {
      if (options.strictAnchorValidation && (txHashBeforeSanitize || toAddressBeforeSanitize)) {
        anchorSanitizedSkips += 1;
      }
      continue;
    }

    const assetRaw = columns.asset >= 0 ? String(row[columns.asset] || '').trim() : '';
    const assetSymbol = assetRaw ? assetRaw.toUpperCase() : '';
    const network = txHash ? inferNetwork(txHash, assetSymbol) : inferNetworkFromAddrOrAsset(toAddress, assetSymbol);
    const canLookupTxHash = !txHash && Boolean(toAddress) && amountRawAbs != null && Boolean(assetSymbol);

    entries.push({
      id: `row-${rowIndex}-${txHash || 'nohash'}-${toAddress || 'noaddr'}`,
      rowIndex,
      txHash,
      toAddress,
      transferType: typeValueRaw || (hasHeader ? 'withdrawal' : ''),
      network,
      amountDisplay,
      amountRaw: amountRawAbs,
      assetSymbol: assetSymbol || null,
      canLookupTxHash,
      lookupContext: !txHash && toAddress
        ? {
            toAddress,
            assetSymbol: assetSymbol || null,
            amountAbs: amountRawAbs,
            timestamp: timestampValue,
            timestampEpoch
          }
        : null,
      status: txHash ? 'ready' : 'needs_hash',
      isSelectable: Boolean(txHash),
      notes: '',
      warnings: buildRowWarnings({ txHash, toAddress, canLookupTxHash }),
      fromAddressCandidate: fromAddressRaw ? (extractFirstAddressFromText(fromAddressRaw) || fromAddressRaw) : ''
    });
  }

  if (amountParseTried > 0 && amountParseFailed > 0) {
    const percent = Math.round((amountParseFailed / amountParseTried) * 100);
    warnings.push(`Amount parse warnings: ${amountParseFailed}/${amountParseTried} values were not numeric after normalization (${percent}%).`);
  }

  if (flags.isLedgerCsvV1) {
    warnings.push('Detected an On-chain Ledger CSV. Rows were limited to Onchain activity.');
  }
  if (flags.isCsvTxidFromToV1) {
    warnings.push('Detected a TxID + From/To export. Rows with a destination address were treated as withdrawal candidates.');
  }
  if (flags.isCsvActivityDetailsV1) {
    warnings.push('Detected an Activity/Details export. Rows were included only when Activity equals withdrawal-crypto.');
  }
  if (flags.isCsvStandardSetC) {
    warnings.push('Detected Standard Set C. Rows were included only when Transaction Kind equals crypto_withdrawal.');
  }
  if (flags.isCsvStandardSetD) {
    warnings.push('Detected Standard Set D. Destination addresses were extracted from the Notes column when available.');
  }
  if (!hasTypeColumn && options.assumeAllWithdrawalsWhenNoType) {
    warnings.push(
      assumedWithdrawalRows > 0
        ? `No transaction-type column was identified, so ${assumedWithdrawalRows} row(s) with strong anchors were assumed to be withdrawals.`
        : 'No transaction-type column was identified, and no rows qualified for the withdrawal assumption.'
    );
  }
  if (anchorSanitizedSkips > 0) {
    warnings.push(`Skipped ${anchorSanitizedSkips} row(s) with header-like or invalid anchor values.`);
  }
  if (engageNotesMode) {
    warnings.push('Notes-mode was active, so destination addresses were extracted from free text when possible.');
  }

  const readyCount = entries.filter((entry) => entry.isSelectable).length;
  const withToAddressCount = entries.filter((entry) => entry.toAddress).length;
  const missingHashCount = entries.filter((entry) => !entry.txHash).length;

  return {
    rows,
    hasHeader,
    headerRowIndex,
    headerRaw,
    headerNorm,
    format: hasHeader ? guessFormat(headerNorm) : null,
    columns,
    detectionStats: hasHeader ? { ...(detected._stats || {}) } : {},
    entries,
    warnings,
    importSummary: {
      candidateCount: entries.length,
      readyCount,
      missingHashCount,
      withToAddressCount,
      invalidCount: 0
    },
    meta: {
      flags: { ...flags },
      fromAddressCol,
      notesCol,
      timeCol,
      directionCol: typeof timeCols.directionCol === 'number' ? timeCols.directionCol : -1,
      hasTypeColumn,
      engageNotesMode,
      ledgerTimeCols: timeCols.ledgerTimeCols || null
    }
  };
}
